import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Location } from "@angular/common";
import { MasterService } from "../../../../services/master.service";
import { MessageService } from "../../../../services/message.service";
import { Router, ActivatedRoute } from "@angular/router";
import * as languageLibrary from '../../../../services/language'
import { StoreService } from "src/app/services/store.service";
import { SUPPORTED_IMAGE_TYPES, SUPPORTED_IMAGE_EXTENSIONS } from "src/app/constants/file";
import { Dealership } from "src/app/models/dealership.model";
import { Timezone } from "src/app/models/timezone.model";

@Component({
    selector: "general-information-corporate",
    templateUrl: "./general-information.component.html",
    styleUrls: ["./general-information.component.scss"],
    standalone: false
})
export class GeneralInformationCorporateComponent implements OnInit {
    /*
    * variables
    */

    // save the language
    @Input() language: string = 'EN'

    // entity
    @Input() entity: string

    // entity id
    @Input() entityId: number

    // save all data by type
    @Input() data: Object | null = null

    // Unique internal identity of the associated contact
    @Input() contactId: number = 0

    @Output() childEmitter$: EventEmitter<Object> = new EventEmitter()

    // set all words
    protected words = languageLibrary.language

    //  define if loading state
    protected loading: boolean = false;

    //  define if some changes not saved
    protected savePending: boolean = false

    // define the contact type
    protected type: string = "";

    // define the contact type
    protected id: number = 0;

    // collapse/expand button
    protected expanded: boolean = true;

    protected generalDetails: Object[] = [];

    //
    protected parsedType: string;

    protected dealershipList: Dealership[];

    protected image = {}

    protected timezones: Timezone[] = []

    protected supportedExtensions: string = SUPPORTED_IMAGE_EXTENSIONS.join(',')

    // Group of inputs
    protected information = new FormGroup({
        id: new FormControl<number>(0, []),
        website: new FormControl<string | null>(null, []),
        firstName: new FormControl<string | null>(null, []),
        lastName: new FormControl<string | null>(null, []),
        companyName: new FormControl<string | null>(null, [Validators.required]),
        dbaName: new FormControl<string | null>(null, []),
        dealerNumber: new FormControl<string | null>(null, [Validators.required]),
        einNumber: new FormControl<string | null>(null, []),
        dealershipId: new FormControl<number>(0),
        contactId: new FormControl<number>(0, [Validators.required]),
        displayLogoInternal: new FormControl<number>(0),
        corporateLogoURL: new FormControl<string | null>(null),
        tzId: new FormControl<number>(0)
    });

    protected dealershipId: any;
    // public contactId: number = 0;

    // get information of inputs
    get website() {
        return this.information.get("website");
    }
    get firstName() {
        return this.information.get("firstName");
    }
    get lastName() {
        return this.information.get("lastName");
    }
    get dbaName() {
        return this.information.get("dbaName");
    }
    get dealerNumber() {
        return this.information.get("dealerNumber");
    }
    get einNumber() {
        return this.information.get("einNumber");
    }
    get companyName() {
        return this.information.get("companyName");
    }

    /*
    * Life cycles
    */
    constructor(private router: Router, private route: ActivatedRoute, private ms: MessageService, private master: MasterService, private store: StoreService, private location: Location) { }

    ngOnInit() {
        this.type = this.route.snapshot.params['type'] || 'corporation'

        if (this.route.snapshot.params["id"] === 'add') {
            this.id = 0
            if (this.type === 'lot') {
                this.information.value["dealershipId"] = this.route.snapshot.queryParams['dealer']
                this.getDealershipList()
                this.fetchTimezones()
            }

            return
        }

        this.id = parseInt(this.route.snapshot.params["id"]);
        if (!this.id || (Object.keys.length == 0)) {
            // this.information.get('entityType').setValue(this.type)
            this.information.get('dealershipId').setValue(this.route.snapshot.queryParams['dealer'])
            return
        }

        if (isNaN(this.contactId) || (this.contactId <= 0)) {
            this.contactId = 0
        }

        if (this.type === 'corporation') {
            this.information.get('id').setValidators([Validators.required]);
            this.information.get('id').updateValueAndValidity();
            this.getCorporationGeneralDetails()
        }

        if (this.type === 'dealership') {
            this.getDealershipGeneralDetails()
        }

        if (this.type === 'lot') {
            this.getLotGeneralDetails()
            this.fetchTimezones()
        }
    }

    /*
    * Functions
    */

    protected getCorporationGeneralDetails() {
        this.master.get(`corporations/${this.id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.corporateDetails, 'corporate')
        })
    }

    protected getDealershipGeneralDetails() {
        this.master.get(`dealerships/${this.id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.dealershipDetails, 'dealership')

        })
    }

    protected getLotGeneralDetails() {
        this.master.get(`lots/${this.id}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.lotDetails, 'lot')
            this.getDealershipList()

        })
    }

    protected setInformation(data: any, type: string): void {
        this.generalDetails = data
        this.id = parseInt(this.generalDetails['id'])
        this.contactId = parseInt(this.generalDetails['contactId'])
        this.setInformationValues()

        this.childEmitter$.emit({ message: 'setIds', contactId: this.contactId, id: this.id });
        this.childEmitter$.emit({ message: 'setDetailInfo', detailInfo: { createdBy: this.generalDetails['createdByUser'] ? this.generalDetails['createdByUser'].firstName + " " + this.generalDetails['createdByUser'].lastName : null, modifiedBy: this.generalDetails['modifiedByUser'] ? this.generalDetails['modifiedByUser'].firstName + " " + this.generalDetails['modifiedByUser'].lastName : null, createdAt: this.generalDetails['createdAt'], updatedAt: this.generalDetails['updatedAt'] } })

    }

    protected setInformationValues() {
        this.information.setValue({
            id: this.id,
            contactId: this.contactId,
            website: this.generalDetails["contact"]["website"] || null,
            firstName: this.generalDetails["contact"]["firstName"] || null,
            lastName: this.generalDetails["contact"]["lastName"] || null,
            companyName: this.generalDetails["contact"]["name"] ? this.generalDetails["contact"]["name"] : this.generalDetails["contact"]["companyName"],
            dbaName: this.generalDetails["dbaName"] || null,
            dealerNumber: this.generalDetails["dealerNumber"] || null,
            einNumber: this.generalDetails["einNumber"] || null,
            dealershipId: this.generalDetails["dealershipId"] || null,
            displayLogoInternal: this.generalDetails["displayLogoInternal"] || false,
            corporateLogoURL: this.generalDetails["corporateLogoURL"] || null,
            tzId: this.generalDetails["tzId"] || null
        })
    }

    protected getDealershipList() {
        this.master.get(`dealerships`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.dealershipList = res.data.dealershipList
        })
    }

    protected save() {
        if (!this.id || isNaN(this.id) || (this.id <= 0)) {
            // SAVE
            if (this.type === 'corporation') {
                return
            }
            if (this.type === 'dealership') {
                this.saveDealershipGeneralDetails()
                return
            }
            if (this.type === 'lot') {
                this.saveLotGeneralDetails()
                return
            }
        }

        // MODIFY
        if (this.type === 'corporation') {
            this.modifyCorporationGeneralDetails()
            return
        }
        if (this.type === 'dealership') {
            this.modifyDealershipGeneralDetails()
            return
        }
        if (this.type === 'lot') {
            this.modifyLotGeneralDetails()
            return
        }
    }

    private saveLotGeneralDetails() {
        if (this.information.invalid) {
            return
        }

        const payload = Object.assign({}, this.information.value)

        this.master.post(`lots/`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.getDealershipList()
            this.setInformation(res.data.lotDetails, 'lot')

        })
    }

    private saveDealershipGeneralDetails() {
        if (this.information.invalid) {
            return
        }

        const payload = Object.assign({}, this.information.value)

        this.master.post(`dealerships/`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.dealershipDetails, 'dealership')
        })
    }


    private modifyLotGeneralDetails() {
        if (this.information.invalid) {
            return
        }

        const payload = Object.assign({}, this.information.value)

        this.master.put(`lots/${this.id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.lotDetails, 'lot')
        })
    }

    private modifyDealershipGeneralDetails() {
        if (this.information.invalid) {
            return
        }

        const payload = Object.assign({}, this.information.value)

        this.master.put(`dealerships/${this.id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.dealershipDetails, 'dealership')
        })
    }

    private modifyCorporationGeneralDetails() {
        if (this.information.invalid) {
            return
        }

        const payload = Object.assign({}, this.information.value)

        this.master.put(`corporations/${this.id}`, { information: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setInformation(res.data.corporationDetails, 'corporate')
        })
    }

    // get iamege and convert to base64
    protected changeImage = (e): void => {
        let files = []
        let reader = []
        let vm = this
        files = e.target.files
        for (let index = 0; index < files.length; index++) {
            // check if some file is invalid
            if (SUPPORTED_IMAGE_TYPES.includes(files[index].type)) {
                // * format valid
                reader[index] = new FileReader();
                reader[index].readAsDataURL(files[index]);
                reader[index].onload = function(event) {
                    vm.image = {
                        'corporateLogoURL': reader[index].result,
                        'display': true,
                    }
                    vm.uploadImage(vm.image['corporateLogoURL'])
                }

            } else {
                // ! invalid format
                this.ms.sendMessage("alert", { type: "danger", text: 'Invalid image format' });
            }
        }
    }
    // remove image
    protected removeImage = () => {
        (document.getElementById('image-upload-input') as HTMLInputElement).value = "";
        let file = this.information.get('corporateLogoURL').value
        this.master.discard(`logo?key=${file}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.image = {}
            this.information.patchValue({
                corporateLogoURL: null,
                displayLogoInternal: 0
            })

            this.ms.channelComponents$.emit({ message: 'reloadLogo', section: 'setup', value: {} })

        });
    }
    // upload image
    private uploadImage = (url: string): void => {
        this.loading = true
        let from = url.indexOf(',')
        let urlShort = url.substr(from + 1, url.length)
        this.master.post(`logo`, { logo: urlShort }, res => {
            this.loading = false
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.image = { corporateLogoURL: res.data.logoUrl, display: true }
            this.information.patchValue({
                corporateLogoURL: res.data.logoUrl,
                displayLogoInternal: 1
            })

            this.ms.channelComponents$.emit({ message: 'reloadLogo', section: 'setup', value: this.image })
        });

    };
    // upload image
    protected toggleDisplay = (display: boolean): void => {
        this.master.patch(`logo`, { display }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.image = { corporateLogoURL: this.information.get('corporateLogoURL').value, display: display }
            this.ms.channelComponents$.emit({ message: 'reloadLogo', section: 'setup', value: this.image })
        });
    }

    private fetchTimezones = () => {
        this.master.get(`timezones`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.timezones = res.data.zones
        })
    }
}
